// API datetimes read back from Mongo arrive naive ("2026-10-01T15:00:00",
// no zone) but are UTC. `new Date()` would read them as local time, so pin
// the zone first. Strings that already carry Z or an offset pass through.
// A Date or number passes straight to `new Date()`. A date-only string
// ("YYYY-MM-DD") is pinned to UTC midnight so it doesn't roll back a day
// west of UTC.
export const fromApi = (iso) => {
  // null/undefined → an Invalid Date, never "nullZ" parsed by accident.
  if (iso == null) return new Date(NaN);
  if (iso instanceof Date || typeof iso === 'number') return new Date(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return new Date(`${iso}T00:00:00Z`);
  return new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`);
};

// Task due dates are a mixed bag: BFY tasks are saved from a date-only
// `<Input type="date">` as UTC midnight (ticket 125), so rendering them in
// local time can roll back a day in zones west of UTC. GHL-mirrored tasks
// carry a real time and should render in the viewer's local zone. Tell them
// apart by checking whether the UTC time is exactly midnight.
export const isDateOnly = (iso) => {
  const d = fromApi(iso);
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
};

export const formatDate = (iso) => {
  if (!iso) return null;
  const d = fromApi(iso);
  return isDateOnly(d) ? d.toLocaleDateString(undefined, { timeZone: 'UTC' }) : d.toLocaleDateString();
};

// Alias used by the task screens (ContactTasks, TasksPage).
export const formatDueDate = formatDate;

// Full date+time display, always rendered in the viewer's local zone.
export const formatDateTime = (iso) => {
  if (!iso) return null;
  return fromApi(iso).toLocaleString();
};

// Where a task's due date sits against today, by calendar day (a task due
// earlier today is "today", not overdue): 'overdue' | 'today' | null. A
// date-only due date is read as its UTC calendar day (see isDateOnly); one
// with a real time (GHL) as the viewer's local day. `now` is injectable for tests.
const dayKey = (y, m, d) => y * 10000 + (m + 1) * 100 + d;
export const dueState = (iso, now = new Date()) => {
  if (!iso) return null;
  const due = fromApi(iso);
  if (Number.isNaN(due.getTime())) return null;
  const dueDay = isDateOnly(due)
    ? dayKey(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate())
    : dayKey(due.getFullYear(), due.getMonth(), due.getDate());
  const today = dayKey(now.getFullYear(), now.getMonth(), now.getDate());
  if (dueDay < today) return 'overdue';
  return dueDay === today ? 'today' : null;
};

// Short date for chips ("Sep 12"), rendered in the viewer's local zone.
export const formatShortDate = (iso) => {
  if (!iso) return null;
  const d = fromApi(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
